import { Button } from '@design-system';
import { isBrowser } from '@utils/isBrowser';
import classnames from 'classnames';
import React, { useState } from 'react';
import { useKey } from 'react-use';

import { DrawerOverridesContext } from '../../../context/DrawerOverrides';
import { useDisableBodyScrolling } from '../../../hooks/useDisableBodyScrolling';
import Portal from '../../Portal';
import DrawerPanel from './DrawerPanel';

type Props = {
  backButtonAction?: () => void;
  onAnimationEnd?: () => void;
  onClose?: () => void;
  overlayOtherDrawers?: boolean;
  showBackButton?: boolean;
  showNavigationBorder?: boolean;
  shown?: boolean;
  title?: string;
  children?: React.ReactNode;
};

export const Drawer: React.FC<Props> = ({
  backButtonAction,
  children,
  onAnimationEnd,
  onClose = () => null,
  overlayOtherDrawers,
  showBackButton = false,
  showNavigationBorder = true,
  shown = false,
  title = '',
}) => {
  useDisableBodyScrolling(shown);
  useKey('Escape', onClose);

  const [showBackButtonOverride, setShowBackButtonOverride] =
    useState<boolean>();
  const [backActionOverride, setBackActionOverride] = useState<() => void>();

  const showBackButtonFinal = showBackButtonOverride || showBackButton;
  const backButtonActionFinal = backActionOverride || backButtonAction;

  if (!isBrowser()) {
    return null;
  }

  return (
    <DrawerOverridesContext.Provider
      value={{
        backButtonAction: backActionOverride,
        setBackButtonAction: setBackActionOverride,
        setShowBackButton: setShowBackButtonOverride,
        showBackButton: showBackButtonOverride,
      }}
    >
      <Portal>
        <div
          className={classnames(
            'fixed left-0 top-0 h-screen w-screen',
            { 'z-drawer': !overlayOtherDrawers },
            { 'z-secondaryDrawer': overlayOtherDrawers },
            shown ? 'pointer-events-auto' : 'pointer-events-none',
          )}
        >
          <div
            aria-label="hide-panel"
            className={classnames(
              'absolute left-0 top-0 h-full w-full transition-all duration-500',
              shown ? 'bg-neutral-6/40' : 'bg-transparent',
            )}
            data-testid="drawer-close-button"
            onClick={onClose}
            onKeyDown={onClose}
            role="button"
            tabIndex={0}
          />
          <DrawerPanel
            className="theme-light top-0 flex h-full flex-col overflow-x-hidden bg-white"
            onTransitionEnd={onAnimationEnd}
            shown={shown}
          >
            <div className="relative flex-1 overflow-x-hidden overflow-y-scroll px-website pt-14 md:pt-18">
              <section className="mx-auto w-full max-w-sm py-9 md:py-12">
                {children}
              </section>
            </div>
          </DrawerPanel>
          <DrawerPanel className="top-0" shown={shown}>
            <div
              className={classnames('theme-light bg-white', {
                'border-b border-divider/20': showNavigationBorder,
              })}
            >
              <div className="wrapper-website flex h-14 items-center justify-between md:h-18">
                <div className="h-9 w-9">
                  {showBackButtonFinal && (
                    <Button
                      hideLabel
                      icon="ic_arrow_left"
                      label="Back"
                      onClick={() => backButtonActionFinal?.()}
                      size="sm"
                      variant="transparent"
                    />
                  )}
                </div>
                <div className="flex flex-grow items-center justify-center text-balance text-center font-bold md:hidden md:text-md">
                  {title}
                </div>
                <div className="h-9 w-9">
                  <Button
                    hideLabel
                    icon="ic_close"
                    label="Close"
                    onClick={onClose}
                    size="sm"
                    variant="transparent"
                  />
                </div>
              </div>
            </div>
          </DrawerPanel>
        </div>
      </Portal>
    </DrawerOverridesContext.Provider>
  );
};

export default Drawer;
